import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import FeatureBlock from '@/components/feature-block'
import Image from '@/components/image'
import {Marquee} from '@/components/marquee'
import PrismicLink from '@/components/prismic-link'
import RichText from '@/components/rich-text'
import type {
    FeatureBlockWithMarqueeFragment,
    FeatureBlockWithMarqueeSecondaryFragment,
} from '@/graphql-types'
import useMediaQuery from '@/hooks/use-media-query'
import {IntegrationBox} from '@/sections/integrations'
import {edgeGradient} from '@/styles/edge-gradient'
import {Container, Section} from '@/styles/grid'
import {tokens} from '@/theme'
import mq from '@/utils/media-query'

interface Props {
    slice: FeatureBlockWithMarqueeFragment | FeatureBlockWithMarqueeSecondaryFragment
}

export const FeatureBlockWithMarquee = React.forwardRef<HTMLElement, Props>(({slice}, ref) => {
    const data = slice.primary
    const isSmallScreen = useMediaQuery.max('lg')

    return (
        <StyledSection ref={ref} hideOverflow>
            <Container>
                <FeatureBlock
                    ctaLink={data.cta_link}
                    ctaText={data.cta_label}
                    image={data.image}
                    preTitle={data.headline}
                    title={data.title}
                    body={data.body1.richText}
                    mirrored={!!data.inverse}
                    headingLevel="h2"
                >
                    {!!data.pre_cta_links && <RichText content={data.pre_cta_links.richText} />}

                    {data.cta_label && (
                        <Button $variant="outline" as={PrismicLink} {...data.cta_link}>
                            {data.cta_label}
                        </Button>
                    )}
                </FeatureBlock>
            </Container>
            {slice.items.length > 0 ? (
                isSmallScreen ? (
                    <IntegrationLogos>
                        <IntegrationsLogos logos={slice.items} />
                    </IntegrationLogos>
                ) : (
                    <MarqueeWrapper>
                        <StyledMarquee itemWidth={150} gap={0} speed={1}>
                            <IntegrationsLogos logos={slice.items} />
                        </StyledMarquee>
                    </MarqueeWrapper>
                )
            ) : null}
        </StyledSection>
    )
})

interface IntegrationLogosProps {
    logos: FeatureBlockWithMarqueeFragment['items']
}

const IntegrationsLogos = ({logos, ...props}: IntegrationLogosProps) => {
    return (
        <>
            {logos.map(({logo, link}) => (
                <IntegrationLogo key={logo.url} {...link} {...props}>
                    <Image image={logo} />
                </IntegrationLogo>
            ))}
        </>
    )
}

const MarqueeWrapper = styled.div`
    width: 100%;
    height: 155px;
`

const IntegrationLogos = styled(Container)`
    display: grid;
    grid-auto-flow: column;
    justify-content: start;
    overflow: scroll;
    gap: ${tokens.spacing12};
    padding-top: ${tokens.spacing2};
    padding-bottom: ${tokens.spacing2};
`

const StyledMarquee = styled(Marquee)`
    ${mq.min('xl')} {
        ${edgeGradient()}
    }
`

const StyledSection = styled(Section)`
    display: grid;
    padding-right: 0;
    padding-left: 0;
    gap: ${tokens.spacing40};

    ${mq.min('md')} {
        gap: 80px;
    }
`

const IntegrationLogo = styled(PrismicLink)`
    ${IntegrationBox}

    align-content: center;
    display: grid;
    height: 136px;
    padding: ${tokens.spacing32};

    ${mq.min('lg')} {
        height: 150px;
        margin-left: 30px;
        margin-right: 30px;
    }
`
